<template>
        <div>
            <p-card :title="`${label} Import`">
                <b-row>
                    <b-col>
                        <vue-csv-import
                            ref="vuecsvimport"
                            :map-fields="headers"
                            :headers="headers"
                            :key="'import' + importCount"
                            :can-run-no-parse="canRunNoParse"
                            :is-job-running-now="isJobRunningNow"
                            @unmapped-fields="unMappedFields = $event"
                            auto-match-fields
                            auto-match-ignore-case
                            @input="fileLoaded"
                            @loading-new-file="loadingNewFile"
                            @error-loading-file="handleFileLoadError"
                            @run-no-parse="handleRunNoParse"
                            @file-name="updateFileName"
                        ></vue-csv-import>
                    </b-col>
                    <b-col cols="auto" class="ms-auto">
                        <p-button @click="downloadSample">Download Sample CSV</p-button>
                    </b-col>
                </b-row>
                <b-row v-if="unMappedFields.length > 0">
                    <b-col>
                        Unable to parse file due to missing column(s):
                    <ul>
                        <li v-for="(header, index) in unMappedFields" :key="'missingField' +index">{{header}}</li>
                    </ul>
                    </b-col>
                </b-row>
                <b-row v-if="hasCaseConversionFields">
                    <b-col>
                        <p-checkbox v-model='convertCase' :label="convertCaseLabel" />
                    </b-col>
                </b-row>
                <b-row v-if="canCreateUsed">
                    <b-col>
                        <p-checkbox :model-value='createUsed'
                            @update:model-value="$emit('update:createUsed', $event)"
                            label="Create used books."
                            info-message="Alternatively individually populate the UsedDCC field on an import row to selectively create a used book." />
                    </b-col>
                </b-row>
            </p-card>
            <p-card :title="`Validated (${validRows.length})`" v-show='showTables' collapse-enabled title-class="text-success" class="mt-3">
                <b-row v-if="importNotes.length > 0  && invalidRows.length < 1">
                    <b-col>
                    <ul>
                        <li v-for="(note, index) in importNotes" :key="'notes' +index" >{{note}}</li>
                    </ul>
                    </b-col>
                </b-row>
                <p-table :items="validRows"
                        :fields="fields"
                        :per-page="20"
                        :enable-row-selection='false'
                >
                </p-table>
            </p-card>
            <b-row v-show='showEditOptions' cols="1" cols-sm="2" cols-xl="4">
                <b-col>
                    <p-advanced-select v-model="editAll" label="Warning/Error Edit Mode" hide-search :options="editOptions" />
                </b-col>
            </b-row>
            <template v-for="(validationMode) in validationModes" :key="validationMode.name">
                <p-card
                            :title-class="validationMode.titleClass"
                            :title="`${validationMode.label} (${validationMode.items.length})`"
                            :start-expanded="validationMode.startExpanded"
                            v-show='showTables'
                            collapse-enabled
                            class="overflow mt-3">
                    <p-form :ref="validationMode.name">
                            <b-row v-if="!validationMode.isError && importNotes.length > 0">
                                <b-col>
                                    <ul>
                                        <li v-for="(note, index) in importNotes" :key="'warningNote' + index">{{note}}</li>
                                    </ul>
                                </b-col>
                            </b-row>
                            <p-table 
                                :class='validationMode.name'
                                :items="validationMode.items"
                                :fields="errorFields"
                                :per-page="5"
                                :empty-text="validationMode.emptyText"
                                :enable-row-selection="validationMode.isError == true"
                                @context-changed="validateInvalidForm"
                                :selection-actions="!validationMode.isError ? [] : [{
                                                label: 'Exclude selection from import',
                                                action: excludeFromImport
                                            },
                                            {
                                                label: `Reinstate ${excludedRows.length} excluded row(s)`,
                                                action: undoExcludeFromImport,
                                                noSelectionRequired: true,
                                                hidden: excludedRows.length < 1,
                                            },
                                            {
                                                label: `Download ${excludedRows.length} excluded row(s)`,
                                                action: downloadExcludedRows,
                                                noSelectionRequired: true,
                                                hidden: excludedRows.length < 1,
                                            }

                                        ]"
                            >
                                <template  v-for="(currentfield, index) in fields" :key="index" v-slot:[`cell(${currentfield.key})`]="{ item, value, field }">
                                    <div>
                                    <p-checkbox v-if="field.inputType === 'checkbox'"
                                        :class="{ 'warning-checkbox-indicator': showWarningIndicator(field, item, validationMode) }"
                                        v-model="item[field.key]"
                                        :label="''">
                                    </p-checkbox>
                                    <div
                                        v-if="validationMode && validationMode.name === 'warningForm' && field.warningMessage && showWarningIndicator(field, item, validationMode)"
                                        class="text-warning small"
                                    >
                                        {{ getWarningMessage(field, item) }}
                                    </div>
                                    <template v-else-if="enableEdit(field, item, validationMode)">
                                        <template v-if="field.inputType === 'date'">
                                            <!-- The datepicker blanks values it can't parse, so hand it null for an unparseable
                                                date and keep 'invalid' on the row until the user picks a real date. -->
                                            <p-datepicker :model-value="item[field.key] === 'invalid' ? null : item[field.key]"
                                                @update:model-value="item[field.key] = $event"
                                                :name="field.label"
                                                :validation='true'
                                                @change="validateInvalidForm();"
                                                :rules="field.addRowDataToRule ? addRowDataToRule(field.addRowDataToRule, field.rules, item) : field.rules"
                                                compact-format />
                                            <div v-if="item[field.key] === 'invalid'" class="text-warning small">
                                                Invalid date: {{ item.originalData[field.key] }}
                                            </div>
                                        </template>
                                        <p-advanced-select v-else-if="field.dataType"
                                            v-model="item[field.key + 'Id']"
                                            :name="field.label"
                                            :validation='true'
                                            :filter="field.selectOptionsFilter ? field.selectOptionsFilter(item) : null"
                                            @change="validateInvalidForm();"
                                            :add-empty-option='includeEmptyOption(field.rules)'
                                            @selectedObject="setDropDownTextValue(item, $event, field);"
                                            :add-invalid-option="{value:-1, text:item.originalData[field.key], abbreviation:item.originalData[field.key], disabled:true}"
                                            :rules="field.addRowDataToRule ? addRowDataToRule(field.addRowDataToRule, field.rules, item) : field.rules"
                                            compact-format
                                            :add-disabled-text=false
                                            :data-type="field.dataType">
                                            <template v-slot:error-message name="error-message">Please select a valid {{field.label || field.key}}</template>
                                        </p-advanced-select>
                                        <p-input v-else
                                            :name="field.label"
                                            compact-format
                                            @change="validateInvalidForm"
                                            v-model="item[field.key]"
                                            :rules="field.addRowDataToRule ? addRowDataToRule(field.addRowDataToRule, field.rules, item) : field.rules"
                                        />
                                    </template>
                                    <template v-else-if="field.inputType !== 'checkbox'">
                                        <template v-if="field.inputType === 'date'">
                                            {{dateFormatter(value)}}
                                        </template>
                                        <template v-else>
                                            {{ value }}
                                        </template>
                                    </template>
                                    </div>
                                </template>
                            </p-table>
                        <b-row>
                            <b-col class="mt-3 text-center">
                                <p-button
                                    :is-busy="isBusy"
                                    variant="outline-primary"
                                    @click="validateData"
                                    >Revalidate</p-button
                                >
                            </b-col>
                        </b-row>
                    </p-form>
                </p-card>
            </template>
            <b-row  v-show='showTables'>
                <b-col class="text-center">
                    <p-button :is-busy="isBusy" @click="submit" :disabled='invalidRows.length > 0' :variant="invalidRows.length === 0 && warningRows.length === 0 ? 'primary' : 'outline-primary' "
                        >Import</p-button
                    >
                </b-col>
            </b-row>
        </div>
</template>

<script>
// jQuery removed - replaced with vanilla JS below
import VueCsvImport from './VueCsvImport.vue';
import Axios from 'axios';
import {downloadFileToBrowser} from "@/components/Common/BrowserDownload.js";
import StoreListManager from "@/components/mixins/StoreListManager.js";
import moment from 'moment';
import {dateFormatter, listToStringFormatter} from "@/components/Common/Formatters.js";
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import SharedValidation from './SharedValidation.js';
import { matchesComparisonValue, comparisonProperty } from './ComparisonProperties.js';

export default {
    inheritAttrs: false,

    mixins: [StoreListManager, SharedValidation],
    computed: {
        errorFields() {
            return this.fields;
        },
        validationModes() {
            return[
                {
                    label:'Warnings',
                    name:'warningForm',
                    startExpanded : false,
                    emptyText:"All data passed warning checks.",
                    items: this.warningRows,
                    titleClass: 'text-warning'
                },
                {
                    label:'Errors',
                    isError : true,
                    name:'invalidForm',
                    startExpanded : true,
                    emptyText: "All data passed validation.  Review data to be imported, then click Import to proceed.",
                    items: this.invalidRows,
                    titleClass: 'text-danger'
                }
            ]
        },
        showEditOptions() {
            return this.showTables && this.validationModes.some(x => x.items?.length > 0)
        },
        headers() {
            return this.fields.filter(x => !x.omitFromCsv).map(x => x.key);
        },
        numberProperties(){
            //columns that need to be converted to numbers
            return this.fields.filter(f => f.convertToNumber);
        },
        hasCaseConversionFields(){
            return this.fields.some(f => f.canConvertCase);
        },
        convertCaseLabel(){
            let fields = this.fields.filter(f => f.canConvertCase)
            .map(x => x.key)
            return `Convert ${listToStringFormatter(fields, 'and')} values to title case on import.`
        },
        dateProperties(){
            //columns that send dates to the api
            return this.fields.filter(f => f.inputType === 'date');
        },
        warningDefaultProperties(){
            //columns with warning level validation that have defaults if validation fails
            return this.fields.filter(f => f.warningDefault !== undefined);
        },
        idProperties(){
            //properties that need to match an id (states matching exsting drop down options)
            return this.fields.filter(f => f.dataType);
        }
    },
    props:{
        fields: {
            type: Array,
            required: true
        },
        sampleNotes: {
            type: Array
        },
        dataType: {
            type: String,
            required: true
        },
        importMode: {
            type: String
        },
        importNotesGenerator: {
            type: Function
        },
        label: {
            type: String,
            required: true
        },
        canRunNoParse: {
            type: Boolean,
            default: false
        },
        isJobRunningNow: {
            type: Boolean,
            default: false
        },
        allCustomerNumbers:{
            type: Map
        },
        canCreateUsed: {
            type: Boolean,
            default: false
        },
        createUsed: {
            type: Boolean,
            default: true
        }
    },
    watch: {
        async createUsed() {
            // Field validators read the parent's createUsed, so rows need revalidating when it changes.
            if (!this.showTables) {
                return;
            }

            await this.validateData();
        }
    },
    components: {
        VueCsvImport
    },

    methods: {
        dateFormatter: dateFormatter,
        excludeFromImport(selection){
            this.excludedRowsSaved = false;
            selection.forEach((x) => {
                this.invalidRows.splice(this.invalidRows.indexOf(x), 1);
                this.uploadedData.splice(this.uploadedData.indexOf(x), 1);
                this.excludedRows.push(x);
            });

        },
        updateFileName(fileName) {
            this.fileName = fileName;
        },
        async undoExcludeFromImport(){
            this.uploadedData.push(...this.excludedRows);
            this.excludedRows = [];
            await this.validateData();
        },
        downloadExcludedRows(){
            let data = this.fields.map(x => x.key).join(',') + ',' + 'IsErrorOrWarning'
            const getExportValue = (row, field) => {
                // Checkbox/UI-only fields are not from CSV, so export current row value.
                const sourceValue = field.inputType === 'checkbox' || row.originalData[field.key] === undefined
                    ? row[field.key]
                    : row.originalData[field.key];

                if (sourceValue === null || sourceValue === undefined) {
                    return "";
                }
                return `"${sourceValue}"`;
            };

            this.excludedRows.forEach((row) => {
                data = data + '\n' + this.fields.map(field => {
                    return getExportValue(row, field);
                }).join(',') + ',' + 'Error'
            })

            this.warningRows.forEach((row) => {
                data = data + '\n' + this.fields.map(field => {
                    return getExportValue(row, field);
                }).join(',') + ',' + 'Warning'
            })
            downloadFileToBrowser(data, this.dataType + "_Problem_Import_Rows.csv", 'application/csv');
            this.excludedRowsSaved = true;
        },
        includeEmptyOption(rules){
            if(rules?.required === true){
                return false;
            }
            if(typeof rules === 'string' && rules.includes("required")){
                return false;
            }
            return true;
        },
        async getNotes(){
            if(this.importNotesGenerator){
                this.importNotes = await this.importNotesGenerator();
            }
        },
        addRowDataToRule(ruleName, rules, item){
            // Shallow clones — JSON.parse(JSON.stringify(...)) silently drops
            // function values from the rule params, breaking custom rules that
            // pass methods like `isValidLedgerNumber: this.isValidLedgerNumber`.
            let newRules = { ...rules };
            //for complex rules, we will make row data avaliable for rule calculations when configured
              if(Array.isArray(ruleName)){
                    ruleName.forEach((rule) => {
                        newRules[rule] = { ...newRules[rule], rowData: { ...item } };
                    })
                return newRules;
            }
            newRules[ruleName] = { ...newRules[ruleName], rowData: { ...item } };
            return newRules;
        },
        handleFileLoadError(){
            //due to security restrictions, if the file is modified while loaded,
            //we need the user to re-select the file to give us access to it again.
            //We will reset the control here and prompt them to reselect.  Without resetting
            //the file input control, selecting the same file does not trigger any events.
            this.importCount++;
            this.$appError('Unable to parse file.  Likely because the file has been modified.  Please reload the file.');
        },
        async downloadSample() {
            let data = this.fields.filter(x => !x.omitFromCsv).map(x => x.key).join(',')

            //grab a sample array to find length, could add a length to configuration if this ends up not being flexable enough
            let sampleFields = this.fields.filter(x => !x.omitFromCsv).find(x => x.samples)?.samples;
            let sampleLength = sampleFields ? sampleFields.length : 0;

            for (let index = 0; index < sampleLength; index++) {
                    let row = await this.getSampleRow(index);
                    data = data + '\n' + row;
            }
            if(this.sampleNotes){
                data = data + '\n\n\nNotes:\n' + this.sampleNotes.join('\n') + '\n' +
                        '"Do not include notes section, or extra lines in a file that is used for importing data."';
            }
            downloadFileToBrowser(data, this.dataType + "_Sample_Import.csv", 'application/csv');
        },
        async getSampleRow(index){
            let values = await Promise.all(this.fields.filter(x => !x.omitFromCsv).map(async x => {
                if(x.samples && x.samples[index] != undefined){
                    return x.samples[index];
                }
                if(x.dataType){
                    let options = await selectListOptionsDataContext.getStoreDropdownData(x.dataType);
                    let list = Array.isArray(options) ? options : options[x.dataType] || [];
                    let item = list[Math.min(1, list.length - 1)];
                    return item ? item[comparisonProperty(x)] : '';
                }
            }));
            return values.join(',');

        },
        enableEdit(field, item, validationMode){
            if (this.editAll){
                return true;
            }
            let fieldName =  field.dataType ? field.key + 'Id' : field.key;

            // In warning mode, only fields with a current warning should be editable.
            // This prevents previously invalid fields (from originalData) from continuing
            // to look like active warning controls after they are fixed and revalidated.
            if (validationMode && validationMode.name === 'warningForm') {
                return field.hasWarningCalculator
                    && field.hasWarningCalculator(item[fieldName], item, false);
            }

            if(field.hasErrorCalculator  && field.hasErrorCalculator(item.originalData[fieldName], item.originalData, true)){
                return true;
            }
            if(field.hasWarningCalculator  && field.hasWarningCalculator(item.originalData[fieldName], item.originalData, true)){
                return true;
            }
            return false;
        },
        async fileLoaded(data) {
            if (!Array.isArray(data)) return;
            this.$emit('update:modelValue', data);
            this.uploadedData = data;

            //saving originalData before applying formatRowData or user updates so we
            //can export the original values and display them in error messages      
            await Promise.all(this.uploadedData.map(async x => {
                x.originalData = await this.matchDropdownObjects({ ...x });
            }));

            await this.validateData();
            this.showTables = true;
        },
        loadingNewFile(){
            this.excludedRows = [];
            this.showTables = false;
        },
        async validateData() {
            try {
                this.isBusy = true;

                await Promise.all(this.uploadedData.map(r => this.formatRowData(r)));

                for (const r of this.uploadedData) {
                    this.validateRow(r);
                }

                // Now update the row lists and UI
                this.validRows = this.uploadedData.filter(x => !x.hasErrors && !x.hasWarnings);
                this.invalidRows = this.uploadedData.filter(x => x.hasErrors);
                this.warningRows = this.uploadedData.filter(x => x.hasWarnings && !x.hasErrors);
                this.validateInvalidForm();

                await this.getNotes();
            } 
            finally {
                this.isBusy = false;
            }
        },
        async formatRowData(row){
            // Trim leading/trailing whitespace from all imported string values before any
            // parsing/validation so stray whitespace doesn't trip validation or dropdown matching.
            this.fields.forEach(f => {
                if (typeof row[f.key] === 'string') {
                    row[f.key] = row[f.key].trim();
                }
            });

            // Handle convertToNull fields (convert empty strings to null)
            const convertToNullFields = this.fields.filter(f => f.convertToNull);
            convertToNullFields.forEach(f => {
                if (row[f.key] === '' || row[f.key] === undefined) {
                    row[f.key] = null;
                    if (f.dataType) {                        
                        row[f.key + 'Id'] = null;
                    }
                }
            });            
            //papa parse has the ability to convert string number values to numbers, but I can't find a way
            //to configure it to only do specific columns.
            this.numberProperties.forEach(f =>
                {
                    if( row[f.key] === '') {row[f.key] = null}
                    let numberValue =  parseFloat(row[f.key]);
                    //if the provided value is not number, keep it as is so the user can see and correct the issue.
                    if(!isNaN(numberValue)) {row[f.key] = numberValue}
                }
            );
            this.dateProperties.forEach(f =>
                {
                    //empty strings throw exception when passed to the api for nullable date objects.
                    if( this.isEmpty(row[f.key])) {
                        row[f.key] = null;
                        return;
                    }
                    var parsedDate = moment(row[f.key],                   
                    this.$validDates, true);
                    if(parsedDate.isValid())
                    {
                        //going with toISOString because the moment parsing was being rejected by the server:
                        //The JSON value could not be converted to System.Nullable`1[System.DateTime].
                        row[f.key] = parsedDate.toISOString();
                    }
                    else{
                        //setting it to 'invalid' if not valid, otherwise the datepicker can set it to weird values that we
                        //don't want depending on the input.  For example "Invalid Date 1" loads as 01/01/2001.
                        //Setting it to null will cause the validation to not trip properly.
                        //Issue stems from p-datepicker parsing the date using new Date() which is too flexible and
                        //can pull dates out of strings that don't have data that resembles a valid date.
                        row[f.key] = 'invalid';
                    }
                }
            );
           await this.matchDropdownObjects(row);

            // Initialize checkbox fields if not present (they are not CSV columns).
            // Default to true if no warning (already a match), false if warning or no calculator.
            this.fields.filter(f => f.inputType === 'checkbox').forEach(f => {
                if (row[f.key] === undefined) {
                    if (f.hasWarningCalculator) {
                        const fieldValue = row[f.dataType ? f.key + 'Id' : f.key];
                        row[f.key] = !f.hasWarningCalculator(fieldValue, row);
                    } else {
                        row[f.key] = false;
                    }
                }
            });
        },    
        async matchDropdownObjects(row) {
            //for elements in the store, make sure they match an option and grab the id.
            for (const f of this.idProperties) {
                // Handle default values for empty fields
                if ((row[f.key] === null || row[f.key] === undefined || row[f.key] === '') && f.defaultValue) {
                    row[f.key] = f.defaultValue;
                }

                // Undefined keys due to a missing colunn in the import file set to a value of -1
                if (row[f.key] === undefined) {
                    row[`${f.key}Id`] = -1;
                    continue;
                }

                //make sure the match grabs from the filtered list
                if (!row[f.key] || row[f.key] === '')
                    continue;

                if(f.dataType === 'customers' && this.allCustomerNumbers)
                {
                    let myMatch = this.allCustomerNumbers.get(row[f.key] + '');
                    row[`${f.key}Id`] = myMatch ? myMatch.value : -1;
                }
                else
                {
                    this.selectListOptions = await selectListOptionsDataContext.getStoreDropdownData(f.dataType);

                    let filterValue = f.selectOptionsFilter ? f.selectOptionsFilter(row) : null;

                    // Use cached options for this data type
                    let match = this.selectListOptions
                        .filter(x => !filterValue || x.filter == filterValue)
                        .find(x => matchesComparisonValue(x, f, row[f.key]));

                    row[`${f.key}Id`] = match ? match.value : -1;
                }
            }
            
            return row;
        },
        setDropDownTextValue(row, selection, field){
            //if an element id has been selected from a drop down, update the text as well.
            row[field.key] = selection[comparisonProperty(field)];
        },
        validateInvalidForm() {
            setTimeout(() => {
                this.$nextTick().then(() => {
                    if (this.$refs.invalidForm && this.$refs.invalidForm[0]) {
                        this.$refs.invalidForm[0].validate();
                        
                        if (this.$refs.warningForm && this.$refs.warningForm[0]) {
                            this.$refs.warningForm[0].validate();
                        }
                    }
                });
            }, 300);	    
        },
        validateRow(row) {
            row.hasErrors = this.fields.some(
                f => f.hasErrorCalculator && f.hasErrorCalculator(row[f.dataType ? f.key + 'Id' : f.key], row)
            );
            row.hasWarnings = this.fields.some(
                f => f.hasWarningCalculator && f.hasWarningCalculator(row[f.dataType ? f.key + 'Id' : f.key], row)
            );
        },
        getWarningMessage(field, row) {
            // Fields can supply a function when the message depends on the row or import options.
            if (typeof field.warningMessage === 'function') {
                return field.warningMessage(row);
            }

            return field.warningMessage;
        },
        showWarningIndicator(field, row, validationMode) {
            if (!validationMode || validationMode.name !== 'warningForm') {
                return false;
            }
            if (!field.hasWarningCalculator) {
                return false;
            }
            const value = row[field.dataType ? field.key + 'Id' : field.key];
            return field.hasWarningCalculator(value, row);
        },
        async submit() {
            this.isBusy = true;
            this.$refs.vuecsvimport.isBusy = true;

            this.uploadedData.forEach(r => 
            {
                this.formatRowData(r);
                this.$nextTick(() => {
                    this.validateRow(r);
                });
            });

            this.$nextTick(async () => {
                if(this.warningRows.length > 0 || (this.excludedRows.length > 0 && !this.excludedRowsSaved)){
                    await this.$bvModal.msgBoxOk('A CSV of the problem rows will be downloaded')
                    this.downloadExcludedRows();
                }

                try{

                    //clone uploaded data so we can apply and submit the warning defaults without losing the values the user actually updated (in case import fails)
                    var dataForImport = this.warningDefaultProperties.length > 0 ? this.uploadedData.map(a => ({...a})) : this.uploadedData;
                    this.warningDefaultProperties.forEach(f =>
                    {
                        //if this.isEmpty(value) or fails warning validaiton then set to warningDefault
                        dataForImport.forEach(row => {
                            let currentValue = row[f.dataType ? f.key + 'Id' : f.key]
                            if(!this.isEmpty(currentValue) && !(f.hasWarningCalculator  &&  f.hasWarningCalculator(currentValue, row))){
                                //has a value without a warning so we don't need to apply default
                                return;
                            }
                            row[f.key] = f.warningDefault;
                            if(f.dataType){
                                row[f.key + 'Id'] =  f.warningDefault
                            }
                        });

                    });
                    
                    await Axios.post('imports/' + this.dataType, { importData: dataForImport, fileName: this.fileName, convertCase: this.convertCase, createUsed: this.createUsed, importMode:this.importMode });
                    this.$appSuccess(`${this.label} imported successfully.`);
                    this.resetImport();
                }
                catch(err){
                    this.$emit('upload-failed');
                    await this.validateData();
                    this.$refs.vuecsvimport.fileSelected = false;
                    this.$refs.vuecsvimport.fileName = "";
                    this.$refs.vuecsvimport.inputKey += 1;
                    this.fileName = null;
                }
                finally{                    
                    this.isBusy = false;
                    this.$refs.vuecsvimport.isBusy = false;
                }
            });
        },
        resetImport(){
            this.unMappedFields = [];
            this.validRows = [];
            this.invalidRows = [];
            this.excludedRows = [];
            this.showTables = false;
            this.importCount++;
            this.$emit('form-reset');
        },
        handleRunNoParse(file){
            this.$emit('run-no-parse', file);
        }
    },

    data() {
        return {
            importNotes:[],
            showTables:false,
            isBusy:false,
            importCount:0,
            unMappedFields: [],
            uploadedData: [], //All rows that have been uploaded (minus rows set to be excluded)
            validRows: [],
            invalidRows: [],
            warningRows: [],
            excludedRows: [],
            excludedRowsSaved: false,
            editAll: false,
            convertCase: true,
            editOptions: [
                { value: false, text: 'Edit Invalid Data' },
                { value: true, text: 'Edit All Data' }
            ],
            fileName: ""
        };
    }
};
</script>

<style scoped>
:deep(td ) {
    min-width: 150px;
    max-width: 300px;
}
:deep(th ) {
    min-width: 150px;
    max-width: 300px;
}
:deep(.invalidForm th:first-child ) {
    min-width: 50px;
    max-width: 50px;
}
:deep(.invalidForm td:first-child ) {
    min-width: 50px;
    max-width: 50px;
}
:deep(table ) {
    width: auto;
    height: auto;
}
:deep(.overflow ) {
    width: 100%;
    overflow: auto;
}

:deep(.warning-checkbox-indicator .custom-control-label::before) {
    border-color: #f0ad4e;
    box-shadow: 0 0 0 2px rgba(240, 173, 78, 0.35);
}

</style>
